import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function TimelineManager() {
  const [timeline, setTimeline] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'ABOUT US', hero_subtitle_bg: 'ЗА НАС', hero_subtitle_de: 'ÜBER UNS',
    hero_title_en: 'Your Trusted', hero_title_bg: 'Вашият доверен', hero_title_de: 'Ihr vertrauenswürdiger',
    hero_title_highlight_en: 'Transport Partner', hero_title_highlight_bg: 'транспортен партньор', hero_title_highlight_de: 'Transportpartner',
    hero_description_en: 'With decades of experience, we deliver reliable transport services across Europe.',
    hero_description_bg: 'С десетилетия опит предоставяме надеждни транспортни услуги в цяла Европа.',
    hero_description_de: 'Mit jahrzehntelanger Erfahrung liefern wir zuverlässige Transportdienstleistungen in ganz Europa.',
    timeline_subtitle_en: 'OUR JOURNEY', timeline_subtitle_bg: 'НАШИЯТ ПЪТ', timeline_subtitle_de: 'UNSER WEG',
    timeline_title_en: 'Company', timeline_title_bg: 'Фирмена', timeline_title_de: 'Unternehmens',
    timeline_title_highlight_en: 'History', timeline_title_highlight_bg: 'история', timeline_title_highlight_de: 'geschichte',
    cta_title_en: 'Ready to Work Together?', cta_title_bg: 'Готови за сътрудничество?', cta_title_de: 'Bereit zur Zusammenarbeit?',
    cta_button_en: 'Contact Us', cta_button_bg: 'Свържете се с нас', cta_button_de: 'Kontaktieren Sie uns'
  });
  
  const defaultForm = {
    year: '',
    title_en: '', title_bg: '', title_de: '',
    description_en: '', description_bg: '', description_de: '',
    order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchTimeline(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/about-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/about-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(`${API}/admin/timeline`);
      setTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/timeline/${editingItem.id}`, formData);
        toast.success('Timeline event updated!');
      } else {
        await axios.post(`${API}/admin/timeline`, formData);
        toast.success('Timeline event added!');
      }
      fetchTimeline();
      setIsFormOpen(false);
      setEditingItem(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/admin/timeline/${id}`);
      toast.success('Deleted!');
      fetchTimeline();
    } catch (error) {
      toast.error('Error deleting');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      year: item.year || '',
      title_en: item.title_en || '',
      title_bg: item.title_bg || '',
      title_de: item.title_de || '',
      description_en: item.description_en || '',
      description_bg: item.description_bg || '',
      description_de: item.description_de || '',
      order: item.order || 0,
      is_active: item.is_active !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const updatePageField = (field, value) => setPageContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">About Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} Hero Section</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} placeholder="Subtitle" className="bg-white" />
                        <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} placeholder="Title" className="bg-white" />
                      </div>
                      <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} placeholder="Title Highlight" className="bg-white" />
                      <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} placeholder="Description" className="bg-white" rows={2} />
                      
                      <h4 className={`font-bold ${lang.text} pt-2`}>{lang.flag} Timeline Section</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`timeline_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`timeline_subtitle_${lang.code}`, e.target.value)} placeholder="Timeline Subtitle" className="bg-white" />
                        <Input value={pageContent[`timeline_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`timeline_title_${lang.code}`, e.target.value)} placeholder="Timeline Title" className="bg-white" />
                      </div>
                      <Input value={pageContent[`timeline_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`timeline_title_highlight_${lang.code}`, e.target.value)} placeholder="Timeline Title Highlight" className="bg-white" />
                      
                      <h4 className={`font-bold ${lang.text} pt-2`}>{lang.flag} CTA</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`cta_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_title_${lang.code}`, e.target.value)} placeholder="CTA Title" className="bg-white" />
                        <Input value={pageContent[`cta_button_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_button_${lang.code}`, e.target.value)} placeholder="CTA Button" className="bg-white" />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Company Timeline</h2>
          <p className="text-sm text-slate-500">Milestones in company history</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingItem(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Year
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingItem ? 'Edit Event' : 'New Event'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Year */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Year *</Label>
                  <Input value={formData.year} onChange={(e) => updateField('year', e.target.value)} required placeholder="e.g., 1991" className="mt-1" />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => updateField('order', parseInt(e.target.value) || 0)} className="mt-1" />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              {/* Language Tabs */}
              <Tabs value={activeLang} onValueChange={setActiveLang}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                      {lang.flag} {lang.name}
                      {formData[`title_${lang.code}`] && <span className="ml-1 text-green-500">✓</span>}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div>
                        <Label>Title {lang.code === 'en' && '*'}</Label>
                        <Input 
                          value={formData[`title_${lang.code}`]} 
                          onChange={(e) => updateField(`title_${lang.code}`, e.target.value)} 
                          className="mt-1 bg-white" 
                          required={lang.code === 'en'}
                          placeholder={lang.code === 'en' ? 'e.g., Company Founded' : ''}
                        />
                      </div>
                      <div>
                        <Label>Description {lang.code === 'en' && '*'}</Label>
                        <Textarea 
                          value={formData[`description_${lang.code}`]} 
                          onChange={(e) => updateField(`description_${lang.code}`, e.target.value)} 
                          className="mt-1 bg-white" 
                          required={lang.code === 'en'}
                          rows={3}
                        />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A]"><Save className="w-4 h-4 mr-2" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Timeline List - Chronological order (oldest first) */}
      <div className="space-y-3">
        {timeline.sort((a, b) => (a.year || 0) - (b.year || 0)).map(item => (
          <Card key={item.id} className={`bg-white rounded-2xl shadow-sm ${!item.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-[#C41E3A]">{item.year}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-slate-900">{item.title_en}</h4>
                <div className="flex gap-1 mt-1">
                  {LANGUAGES.map(lang => (
                    <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${item[`title_${lang.code}`] ? lang.bg + ' ' + lang.text : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description_en}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(item)}><Edit2 className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {timeline.length === 0 && <p className="text-slate-500 text-center py-8">No timeline events yet.</p>}
      </div>
    </div>
  );
}
